import * as Popover from '@radix-ui/react-popover'
import { useEffect, useRef, useContext, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { MedSummaryEntryEditActions } from '../store/entries-edit.slice'
import { PopoverContainerContext } from '@/context/popover-container-context'
import { EditableEntryField, FIELD_CHARACTER_LIMITS, getMaskConfig } from '../editable-field-config'
import { AlertErrorCircle } from '@/assets/icons'
import clsx from 'clsx'
import { selectActiveEditPendingValue, selectActiveEditValidationError } from '../store/entries-edit.selectors'
import { useIMask } from 'react-imask'
import { DEFAULT_VALIDATION_ERROR } from '../utils/validation'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'

interface EditFieldPopoverProps {
  isOpen: boolean
  fieldKey: EditableEntryField
  children: React.ReactNode
}

/**
 * Edit Field Popover
 *
 * Popover that overlays the cell when editing. Anchored to top-left and right of the cell,
 * expands vertically to fit content.
 */
export default function EditFieldPopover(props: EditFieldPopoverProps) {
  const { isOpen, fieldKey, children } = props
  const dispatch = useAppDispatch()
  const isTouchOnlyDevice = useIsTouchOnlyDevice()
  const fieldRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null)
  const containerEl = useContext(PopoverContainerContext)
  const triggerRef = useRef<HTMLDivElement>(null)

  // Redux state
  const validationError = useAppSelector(selectActiveEditValidationError)
  const pendingValue = useAppSelector(selectActiveEditPendingValue)

  // Inferred values
  const maxLength = FIELD_CHARACTER_LIMITS[fieldKey]
  const isMultiline = [EditableEntryField.FACILITY_NAME, EditableEntryField.COMMENT, EditableEntryField.PROCEDURE_CODE].includes(fieldKey)
  const maskConfig = getMaskConfig(fieldKey)
  const hasMask = !!maskConfig

  // useIMask hook for masked inputs
  const { ref: maskInputRef, maskRef: maskInstanceRef } = useIMask(maskConfig || {}, {
    onAccept: (value) => {
      if (hasMask) {
        dispatch(
          MedSummaryEntryEditActions.updatePendingValue({
            pendingValue: value,
          })
        )
      }
    },
  })

  /**
   * Determine popover width strategy
   * - If cell fits in the visible width of the container: match cell width and align to cell edge
   * - If cell is too wide: cap at container visible width and let Radix position it
   *
   * We use the container's clientWidth to get the actual available space,
   * which automatically accounts for sidebar, content viewer, etc.
   */
  const [containerVisibleWidth, setContainerVisibleWidth] = useState(0)
  const [cellFitsInContainer, setCellFitsInContainer] = useState(true)

  // Track the visible width of the scrollable container
  useEffect(() => {
    if (!containerEl) return

    const updateWidth = () => {
      // clientWidth gives us the visible width
      setContainerVisibleWidth(containerEl.clientWidth)
    }

    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(containerEl)

    return () => resizeObserver.disconnect()
  }, [containerEl])

  // Check if cell fits in the container's visible width
  useEffect(() => {
    if (isOpen && triggerRef.current && containerVisibleWidth > 0) {
      const cellWidth = triggerRef.current.offsetWidth
      setCellFitsInContainer(cellWidth <= containerVisibleWidth)
    }
  }, [isOpen, containerVisibleWidth])

  // Auto-resize textarea whenever content changes
  useEffect(() => {
    if (isOpen && fieldRef.current instanceof HTMLTextAreaElement) {
      const textarea = fieldRef.current

      const resize = () => {
        if (textarea.offsetWidth > 0) {
          textarea.style.height = '0px'
          textarea.style.height = `${textarea.scrollHeight}px`
        }
      }
      // Initial resize after layout
      requestAnimationFrame(() => {
        resize()
      })
      // Watch for width changes and re-measure
      const resizeObserver = new ResizeObserver(() => {
        resize()
      })
      resizeObserver.observe(textarea)
      // Cleanup
      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [isOpen, pendingValue])

  // Auto-focus input when popover opens
  useEffect(() => {
    if (isOpen) {
      const inputEl = hasMask ? (maskInputRef.current as HTMLInputElement | null) : fieldRef.current
      if (inputEl) {
        inputEl.focus()
        // Position cursor at end of text for easier editing/correction
        const length = inputEl.value.length
        inputEl.setSelectionRange(length, length)
      }
    }
  }, [hasMask, isOpen, maskInputRef])

  // Sync initial value for masked inputs when popover opens
  useEffect(() => {
    if (isOpen && hasMask) {
      if (maskInstanceRef.current) {
        maskInstanceRef.current.value = pendingValue
      }
      // after the value has been set, adjust cursor position
      setTimeout(() => {
        const inputEl = maskInputRef.current as HTMLInputElement | null
        if (inputEl) {
          // Position cursor at end of text for easier editing/correction
          const length = inputEl.value.length
          inputEl.setSelectionRange(length, length)
          // Scroll to the end of the input to ensure the cursor is visible
          inputEl.scrollLeft = inputEl.scrollWidth
        }
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to sync initial value when the popover opens and not on changes to the pending value
  }, [isOpen])

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    dispatch(MedSummaryEntryEditActions.updatePendingValue({ pendingValue: e.target.value }))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Allow Shift+Enter to create line breaks in comment fields on desktop
      if (fieldKey === EditableEntryField.COMMENT && !isTouchOnlyDevice && e.shiftKey) {
        return
      }

      // Enter key saves the field
      e.preventDefault()
      dispatch(MedSummaryEntryEditActions.saveFieldEdit({ autoCancelOnError: false }))
    }
  }

  /**
   * Handles saving or dismissing when user interacts outside the field
   *
   * Used by:
   * - onInteractOutside (all devices) - clicking/tapping outside the popover
   * - onBlur (touch devices only) - to enable saving via mobile iOS keyboard toolbar Done (checkmark) button
   */
  const handleInteractOutsideOrBlur = () => {
    if (validationError) {
      // Error will auto-dismiss: ignore click to avoid dismissing the error prematurely
      if (validationError.autoDismiss) return

      // Error won't auto-dismiss: allow click outside to cancel
      dispatch(MedSummaryEntryEditActions.cancelEditField())
      return
    }

    // No existing validation error: trigger save
    dispatch(MedSummaryEntryEditActions.saveFieldEdit({ autoCancelOnError: true }))
  }

  // Only mount popover when actively editing to reduce rendering overhead
  if (!isOpen) {
    return <>{children}</>
  }

  return (
    <Popover.Root open={isOpen}>
      <Popover.Trigger asChild>
        <div ref={triggerRef} className="w-full">
          {children}
        </div>
      </Popover.Trigger>

      <Popover.Portal container={containerEl || undefined}>
        <Popover.Content
          className={clsx(
            // Base styles
            'p-0 bg-white border rounded z-50 outline-none shadow-[0_0_6px_-2px_rgba(0,0,0,0.05),_0_0_15px_-3px_rgba(0,0,0,0.10)] box-border max-h-[300px] overflow-y-auto pointer-events-auto',
            validationError ? 'border-red-400' : 'border-brand-400',
            // The popover is sized slightly larger than the cell to create a visual overlay that completely covers the cell
            `w-[calc(var(--radix-popover-trigger-width)+2px)]`, // Match cell width + border offset to make popover slightly larger than cell
            `min-h-[calc(var(--radix-popover-trigger-height)+2px)]`, // Match cell height + border offset to make popover slightly larger than cell

            // Vertical positioning: Move popover up/down to overlay the cell
            // Use data-side attribute to conditionally apply margins based on Radix's actual positioning
            `[&[data-side=bottom]]:-mt-[calc(var(--radix-popover-trigger-height)+1px)]`,
            `[&[data-side=top]]:-mb-[calc(var(--radix-popover-trigger-height)+1px)]`
          )}
          style={{
            // When the cell is too wide for the container visible width, cap it (use inline styling so we can use the dynamic value)
            ...(!cellFitsInContainer && containerVisibleWidth > 0 && { maxWidth: `${containerVisibleWidth}px` }),
          }}
          side="bottom"
          align="start"
          sideOffset={0}
          // Align the popover edge with the cell edge using a negative align offset for the overlay effect described above
          // If the cell is larger than the container, no need to shift since the popover will be capped at the container width
          alignOffset={cellFitsInContainer ? -1 : 0}
          avoidCollisions={true}
          collisionBoundary={containerEl || undefined}
          collisionPadding={0}
          onOpenAutoFocus={(e) => e.preventDefault()} // Prevent Radix from focusing, we handle it manually
          onEscapeKeyDown={() => dispatch(MedSummaryEntryEditActions.cancelEditField())}
          onInteractOutside={handleInteractOutsideOrBlur}
        >
          {isMultiline ? (
            <textarea
              ref={fieldRef as React.RefObject<HTMLTextAreaElement>}
              value={pendingValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              {...(isTouchOnlyDevice && { onBlur: handleInteractOutsideOrBlur })}
              maxLength={maxLength}
              enterKeyHint="done"
              /**
               * Mobile browsers (e.g. iOS Safari) auto-zoom focused inputs when font-size < 16px for accessibility.
               * This causes viewport zoom and horizontal scrolling that does not reliably reset.
               * To prevent this, we use 16px on mobile to prevent auto-zoom, and keep 14px on desktop for visual alignment with view-mode.
               */
              className="w-full text-base sm:text-sm resize-none outline-none focus:outline-none focus:ring-0 border-0 rounded leading-[1.5] px-4 py-[10.5px] block box-border overflow-hidden"
            />
          ) : hasMask ? (
            <input
              ref={maskInputRef as React.RefObject<HTMLInputElement>}
              type="text"
              // Specify input mode to show the correct keyboard on mobile: decimal for amount, numeric for service date
              inputMode={fieldKey === EditableEntryField.AMOUNT ? 'decimal' : 'numeric'}
              enterKeyHint="done"
              onKeyDown={handleKeyDown}
              {...(isTouchOnlyDevice && { onBlur: handleInteractOutsideOrBlur })}
              placeholder={fieldKey === EditableEntryField.SERVICE_DATE ? 'MM/DD/YYYY' : undefined}
              className={clsx(
                'w-full h-full text-base sm:text-sm outline-none focus:outline-none focus:ring-0 border-0 rounded px-4 py-[10.5px] block box-border placeholder:text-brand-neutral-600',
                fieldKey === EditableEntryField.AMOUNT && 'text-right'
              )}
            />
          ) : (
            <input
              ref={fieldRef as React.RefObject<HTMLInputElement>}
              type="text"
              enterKeyHint="done"
              value={pendingValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              {...(isTouchOnlyDevice && { onBlur: handleInteractOutsideOrBlur })}
              maxLength={maxLength}
              className="w-full h-full text-base sm:text-sm outline-none focus:outline-none focus:ring-0 border-0 rounded px-4 py-[10.5px] block box-border"
            />
          )}
          {validationError && (
            <div className="p-2 bg-red-50 flex items-center gap-1 text-red-800">
              <AlertErrorCircle className="w-3.5 h-3.5" />
              <div className="text-xs">{validationError.message || DEFAULT_VALIDATION_ERROR}</div>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
